import { PALETTE, getMessageTypeFromPalette } from "../workflowPaletteConstants";
import {
  applyMediaUploadMeta,
  getAutoSaveToContext,
  normalizeButtonOptions,
  normalizeCollectInputConfig,
  normalizeListItems
} from "./workflowInspectorFieldUx";
import { applyMediaUploadResultToNodeConfig } from "../workflowMediaUtils";

export function normalizeNodeConfigForPalette(config = {}, paletteType, nodeLabel = "") {
  let next = { ...config };

  if (paletteType === "whatsapp.notify" || paletteType === "whatsapp.await") {
    if (!next.message) {
      next.message = { type: "text", text: "" };
    } else if (!next.message.type) {
      next.message = {
        ...next.message,
        type: "text",
        text: next.message.text ?? ""
      };
    }
  }

  if (paletteType === PALETTE.COLLECT_INPUT) {
    next = normalizeCollectInputConfig(next, nodeLabel);
  }

  const fixedMessageType = getMessageTypeFromPalette(paletteType);
  if (fixedMessageType) {
    next = {
      ...next,
      message: {
        ...(next.message || {}),
        type: fixedMessageType,
      },
    };
    delete next.type;
  }

  const isButtons = /\.buttons(\.|$)/.test(paletteType) ||
    (paletteType === "whatsapp.await" && next.message?.type === "buttons");
  const isList = /\.list(\.|$)/.test(paletteType) ||
    (paletteType === "whatsapp.await" && next.message?.type === "list");

  if (isButtons && next.message) {
    next = {
      ...next,
      message: {
        ...next.message,
        options: Array.isArray(next.message.options) ? normalizeButtonOptions(next.message.options) : []
      }
    };
  }

  if (isList && next.message) {
    next = {
      ...next,
      message: {
        ...next.message,
        items: Array.isArray(next.message.items) ? normalizeListItems(next.message.items) : []
      }
    };
  }

  return next;
}

/** Merge inspector patches into the latest node config (avoids stale-closure overwrites). */
export function mergeNodeConfigPatch(prevConfig = {}, patch = {}) {
  if (!patch || typeof patch !== "object") return prevConfig || {};

  if (patch.action !== undefined && patch.action !== prevConfig.action) {
    return { ...prevConfig, ...patch };
  }

  if (patch.type !== undefined && patch.type !== prevConfig.type) {
    return { ...prevConfig, ...patch };
  }

  const next = { ...prevConfig, ...patch };

  if (patch.message && typeof patch.message === "object") {
    next.message = { ...(prevConfig.message || {}), ...patch.message };
    if (prevConfig.message) {
      Object.keys(prevConfig.message).forEach((key) => {
        if (!(key in patch.message)) {
          delete next.message[key];
        }
      });
    }
  }

  if (patch.input && typeof patch.input === "object") {
    next.input = { ...(prevConfig.input || {}), ...patch.input };
    if (prevConfig.input) {
      Object.keys(prevConfig.input).forEach((key) => {
        if (!(key in patch.input)) {
          delete next.input[key];
        }
      });
    }
  }

  return next;
}

const EMPTY_MESSAGE_FIELDS = new Set(["url", "caption", "filename", "text", "mediaId", "headerImageMediaId"]);

const ALLOWED_MESSAGE_FIELDS_BY_TYPE = {
  text: ["type", "text"],
  image: ["type", "url", "caption", "mediaId"],
  video: ["type", "url", "caption", "mediaId"],
  document: ["type", "url", "caption", "filename", "mediaId"],
  buttons: ["type", "text", "options"],
  list: ["type", "text", "buttonLabel", "sectionTitle", "items"],
  template: ["type", "templateId", "templateName", "name", "language", "bodyParams", "headerImageLink", "headerImageMediaId", "components"]
};

function cloneAndStripPendingFile(obj) {
  if (!obj || typeof obj !== "object") return obj;
  if (obj instanceof File) return undefined;
  if (Array.isArray(obj)) {
    return obj.map(item => cloneAndStripPendingFile(item));
  }
  const copy = {};
  for (const key of Object.keys(obj)) {
    if (key === "pendingFile") continue;
    const val = obj[key];
    if (val && typeof val === "object" && !(val instanceof File)) {
      copy[key] = cloneAndStripPendingFile(val);
    } else if (val instanceof File) {
      // skip
    } else {
      copy[key] = val;
    }
  }
  return copy;
}

/** Strip empty optional fields before SAVE_WORKFLOW (workflow uses url, not mediaId). */
export function sanitizeNodeConfigForSave(config = {}, paletteType) {
  const next = cloneAndStripPendingFile(config);
  if (!next.message || typeof next.message !== "object") return next;

  let message = { ...next.message };
  const activeType = message.type || "text";
  const allowedFields = ALLOWED_MESSAGE_FIELDS_BY_TYPE[activeType];
  if (allowedFields) {
    const allowedSet = new Set(allowedFields);
    Object.keys(message).forEach((key) => {
      if (!allowedSet.has(key)) {
        delete message[key];
      }
    });
  }

  EMPTY_MESSAGE_FIELDS.forEach((key) => {
    const value = message[key];
    if (value === "" || value == null) {
      delete message[key];
    }
  });

  const fixedType = getMessageTypeFromPalette(paletteType);
  if (fixedType) {
    message.type = fixedType;
  }

  next.message = message;
  return next;
}

export function getFlatVariables(input) {
  if (!input) return {};
  const targetKey = ["variables", "params"].find((k) => k in input);
  const rawVars = targetKey ? input[targetKey] : input;

  if (Array.isArray(rawVars)) {
    return rawVars.reduce((acc, row) => {
      if (row && typeof row === "object" && row.key && String(row.key).trim()) {
        acc[String(row.key).trim()] = row.value ?? "";
      }
      return acc;
    }, {});
  } else if (rawVars && typeof rawVars === "object") {
    const next = {};
    Object.entries(rawVars).forEach(([k, v]) => {
      if (k && typeof v !== "object") {
        next[k.trim()] = String(v ?? "");
      }
    });
    return next;
  }
  return {};
}

export function getClearKeys(input) {
  if (!input) return [];
  const targetKey = ["variables", "keys", "params"].find((k) => k in input);
  const rawKeys = targetKey ? input[targetKey] : input;

  if (Array.isArray(rawKeys)) {
    return rawKeys
      .map(item => {
        if (item && typeof item === "object") {
          return item.key || item.value || "";
        }
        return item;
      })
      .filter(Boolean)
      .map(String)
      .map(s => s.trim());
  } else if (rawKeys && typeof rawKeys === "object") {
    return Object.keys(rawKeys).filter(Boolean).map(String).map(s => s.trim());
  }
  return [];
}

export function applyAutoSaveToContext(data = {}) {
  let nextData = { ...data };

  if (data.workflowType === "ACTION" || data.paletteType === PALETTE.ACTION) {
    const config = data.config || {};
    if (config.action === "SET_VARIABLE") {
      const flatVars = getFlatVariables(config.input);
      const computedSaveToContext = {};
      Object.keys(flatVars).forEach(key => {
        computedSaveToContext[key] = `$result.${key}`;
      });
      nextData.saveToContext = computedSaveToContext;
      return nextData;
    } else if (config.action === "CLEAR_VARIABLE") {
      const clearKeys = getClearKeys(config.input);
      const computedSaveToContext = {};
      clearKeys.forEach(key => {
        computedSaveToContext[key] = `$result.${key}`;
      });
      nextData.saveToContext = computedSaveToContext;
      return nextData;
    }
  }

  const auto = getAutoSaveToContext(data.paletteType);
  if (!auto) return nextData;
  return { ...nextData, saveToContext: { ...auto, ...(nextData.saveToContext || {}) } };
}

/** Apply CRM media upload result onto a single node's canvas data. */
export function buildNodeDataWithMediaUpload(prevData, { url, filename, path }) {
  if (!prevData || !url) return prevData;

  let nextConfig;
  if (path) {
    nextConfig = applyMediaUploadResultToNodeConfig(prevData.config || {}, path, url, filename);
  } else {
    // Fallback/backward compatibility
    const message = applyMediaUploadMeta(
      { ...(prevData.config?.message || {}), url },
      "url",
      { filename: filename || "", clearMediaId: true }
    );
    nextConfig = mergeNodeConfigPatch(prevData.config || {}, { message });
  }

  const normalizedConfig = normalizeNodeConfigForPalette(
    nextConfig,
    prevData.paletteType,
    prevData.label
  );

  return applyAutoSaveToContext({
    ...prevData,
    config: normalizedConfig,
    hasError: false,
  });
}

/** Apply upload result across the canvas; returns a new nodes array. */
export function applyMediaUploadResultToNodes(nodes, uploadResult) {
  if (!uploadResult?.nodeId || !uploadResult?.url || !Array.isArray(nodes)) {
    return nodes;
  }

  return nodes.map((node) => {
    if (node.id !== uploadResult.nodeId) return node;
    const nextData = buildNodeDataWithMediaUpload(node.data, uploadResult);
    return nextData ? { ...node, data: nextData } : node;
  });
}
